// The workspace's one search. The sidebar's box writes it; the overview on
// an empty board reads it; so both show what the same sessions used.
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useQuery } from "./dataClient.js";
import { useFlaggedReach } from "./flaggedReach.js";
import { reviewPlaces, useFleetFindings } from "./fleetFindings.js";
import { pipelineFilterText } from "./queryPipeline.js";
import { flaggedOnly, splitReview } from "./reviewClause.js";
import { SESSION_MATCH_CAP, sessionClauses } from "./sessionMatch.js";

let text = "";
const subscribers = new Set();

export function setWorkspaceSearch(next) {
  const value = typeof next === "function" ? next(text) : next;
  if (value === text) return;
  text = String(value ?? "");
  for (const fn of subscribers) fn();
}

const subscribe = (fn) => {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
};
const read = () => text;

export function useWorkspaceSearch() {
  return [useSyncExternalStore(subscribe, read, read), setWorkspaceSearch];
}

// The sessions picked into the search box one by one ({ id, title }), to
// start a workspace with: held beside the search's clauses rather than in
// them, so picking one does not narrow what the search reaches (and the
// list it was picked from stays as it was, to pick more).
let picks = [];
const pickSubscribers = new Set();
export function setWorkspacePicks(next) {
  const value = typeof next === "function" ? next(picks) : next;
  if (value === picks) return;
  picks = Array.isArray(value) ? value : [];
  for (const fn of pickSubscribers) fn();
}
const subscribePicks = (fn) => {
  pickSubscribers.add(fn);
  return () => pickSubscribers.delete(fn);
};
const readPicks = () => picks;
export function useWorkspacePicks() {
  return [useSyncExternalStore(subscribePicks, readPicks, readPicks), setWorkspacePicks];
}
// A session in, or out if it is in; and many in at once (those not in yet).
export const togglePick = (item) => setWorkspacePicks((list) => (list.some((x) => x.id === item.id) ? list.filter((x) => x.id !== item.id) : [...list, { id: item.id, title: item.title || "" }]));
export const addPicks = (items) => setWorkspacePicks((list) => {
  const have = new Set(list.map((x) => x.id));
  const more = items.filter((x) => x?.id && !have.has(x.id)).map((x) => ({ id: x.id, title: x.title || "" }));
  return more.length ? [...list, ...more] : list;
});

const NO_SCOPE = Object.freeze([]);
const NO_MATCH = Object.freeze({ sessions: Object.freeze([]), clauses: Object.freeze([]), capped: false, prefiltered: false });

// The sessions a search matches (getSessionMatches), its review clauses
// (lib/reviewClause.js) applied to them from where the range's findings left
// each session. Clauses only flagged sessions pass are asked of the findings'
// own sessions, every one in the range (lib/flaggedReach.js, the read the
// page's counts share, so drilling to a place asks nothing more), and the
// newest SESSION_MATCH_CAP of them kept, as the match keeps; one passing
// sessions with nothing flagged, of the newest the rest of the search
// matches. `scopePath` narrows the match to a list of sessions, as the
// match's own does.
export function useSessionMatch(range, q, { scopePath = NO_SCOPE, enabled = true } = {}) {
  const { rest, keeps } = useMemo(() => splitReview(q), [q]);
  const flagged = flaggedOnly(keeps);
  const fleet = useFleetFindings(range, { enabled: enabled && !!keeps });
  const places = useMemo(() => (keeps && fleet.status === "done" ? reviewPlaces(fleet.findings) : null), [keeps, fleet.status, fleet.findings]);
  const ids = useMemo(() => {
    if (!flagged || !places) return null;
    const within = scopePath.find((s) => s.dim === "sessions")?.ids;
    const inScope = within ? new Set(within.map((id) => String(id).toLowerCase())) : null;
    return [...places].filter(([id, place]) => keeps(place) && (!inScope || inScope.has(id))).map(([id]) => id);
  }, [flagged, keeps, places, scopePath]);
  const reach = useFlaggedReach(range, rest, ids?.length ? ids : null, { enabled });
  const match = useQuery("getSessionMatches", { scopePath, range, query: rest }, { enabled: enabled && !flagged && (!keeps || !!places) });
  // Kept while what it is made of holds still: the boards read from it lay
  // out again whenever its sessions are a new list.
  const kept = useMemo(() => {
    if (!keeps) return null;
    if (fleet.status === "error") return { data: undefined, loading: false, error: fleet.error || new Error("The review could not be read") };
    if (!places) return { data: undefined, loading: enabled, error: null };
    if (flagged) {
      if (!ids.length) return { data: NO_MATCH, loading: false, error: null };
      if (reach.status === "error") return { data: undefined, loading: false, error: reach.error };
      if (reach.status !== "done") return { data: undefined, loading: true, error: null };
      const all = ids.map((id) => reach.reached.get(String(id).toLowerCase())).filter(Boolean).sort(newestFirst);
      return { data: { ...reach.meta, sessions: all.slice(0, SESSION_MATCH_CAP), capped: all.length > SESSION_MATCH_CAP }, loading: false, error: null };
    }
    if (!match.data) return { data: undefined, loading: match.loading, error: match.error };
    return { data: { ...match.data, sessions: match.data.sessions.filter((s) => keeps(places.get(String(s.id).toLowerCase()) || "none")) }, loading: match.loading, error: match.error };
  }, [keeps, flagged, ids, reach, match.data, match.loading, match.error, fleet.status, fleet.error, places, enabled]);
  return kept || match;
}
// The match's own order: the newest activity first.
const newestFirst = (a, b) => (Number(b.lastMs) || 0) - (Number(a.lastMs) || 0) || String(a.id).localeCompare(String(b.id));
// Where review left every session a search reaches, not only the newest a
// board is drawn from: how many at each flagged place. The flagged sessions
// are the range's findings' (reviewPlaces), counted only where the session
// match reaches them, as a click on a place lists them: a session the search
// reaches, active in the range (one reviewed in the range but not active in
// it is not). Read through lib/flaggedReach.js, which the drill shares.
// Null until read, or while not `enabled`.
export function useReviewReach(range, q, { enabled = true } = {}) {
  const fleet = useFleetFindings(range, { enabled });
  const places = useMemo(() => (fleet.status === "done" ? reviewPlaces(fleet.findings) : null), [fleet.status, fleet.findings]);
  // The search as the match asks it; its review clauses keep places here.
  const { rest, keeps } = useMemo(() => splitReview(appendClause(q || "", "-review:none")), [q]);
  const ids = useMemo(() => (places ? [...places].filter(([, place]) => keeps(place)).map(([id]) => id) : null), [places, keeps]);
  const reach = useFlaggedReach(range, rest, ids?.length ? ids : null, { enabled });
  return useMemo(() => {
    if (!enabled || !ids) return null;
    const counts = { met: 0, not_verified: 0, not_met: 0, set_aside: 0 };
    if (!ids.length) return counts;
    if (reach.status !== "done") return null;
    for (const id of ids) {
      if (!reach.reached.has(String(id).toLowerCase())) continue;
      const place = places.get(id);
      if (place in counts) counts[place] += 1;
    }
    return counts;
  }, [enabled, ids, reach, places]);
}

// Review clauses no session passes.
const NO_SCOPE_LEFT = Object.freeze([]);
// A session id none has: what reads are scoped to when a search keeps none.
const NO_SESSION = "00000000-0000-0000-0000-000000000000";
const NOTHING_REACHED = Object.freeze([Object.freeze({ dim: "sessions", ids: Object.freeze([NO_SESSION]), id: NO_SESSION })]);

// What the search reaches, as a read's scope: with no search, the range;
// under the match's cap, the sessions it matches (every request of each);
// past the cap, the requests the search itself matches. Review clauses are
// read in the browser, so with them the scope is the sessions they keep
// whatever the cap, and keeping none, a session none has. `ready` waits for
// the match, so nothing is read twice.
export function useSearchScope(range, { delayMs = 400 } = {}) {
  const [live] = useWorkspaceSearch();
  const [q, setQ] = useState(() => pipelineFilterText(live));
  useEffect(() => {
    const t = setTimeout(() => setQ(pipelineFilterText(live)), delayMs);
    return () => clearTimeout(t);
  }, [live, delayMs]);
  const match = useSessionMatch(range, q, { enabled: !!q });
  const reviewed = useMemo(() => !!splitReview(q).keeps, [q]);
  if (!q) return { q, ready: true, scopePath: [], steer: "", match, capped: false, sessions: null };
  const sessions = match.data?.sessions || null;
  const capped = !!match.data?.capped;
  const whole = !!sessions?.length && (!capped || reviewed);
  return {
    q,
    ready: !!match.data,
    scopePath: whole ? [{ dim: "sessions", ids: sessions.map((s) => s.id), id: sessions[0].id }] : reviewed && sessions ? NOTHING_REACHED : [],
    steer: whole || reviewed ? "" : q,
    match,
    capped,
    sessions,
  };
}

// Whether a search already holds a clause (each of its parts, as the search
// reads them): narrowing to it again would say nothing new. Review clauses
// are read as lib/reviewClause.js reads them.
const clauseKeys = (text) => [
  ...sessionClauses(text).clauses.map((c) => `${c.negated ? "-" : ""}${c.text}`),
  ...splitReview(text).tokens.map((k) => `${k.negated ? "-" : ""}${k.text}`),
];
export function holdsClause(current, clause) {
  if (!clause) return false;
  const have = new Set(clauseKeys(pipelineFilterText(String(current ?? ""))));
  const want = clauseKeys(clause);
  return want.length > 0 && want.every((k) => have.has(k));
}

// A clause added to a search: before any aggregate (`|>`), so the pipeline
// keeps running over what the search now matches. One it holds already is
// not added again.
export function appendClause(current, clause) {
  const t = String(current ?? "");
  if (holdsClause(t, clause)) return t;
  const at = t.indexOf("|>");
  const head = (at >= 0 ? t.slice(0, at) : t).trim();
  const tail = at >= 0 ? ` ${t.slice(at)}` : "";
  return `${head ? `${head} ${clause}` : clause}${tail}`;
}
